import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import {
  IconArrowLeft,
  PermissionEnum,
  ResponseError,
  usePrevious,
  getResponseErrors,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearTariffPageState, getLogs } from './store/slices/tariff-page';
import GeneralInfo from '../../components/tariffs/general-info';
import Fees from '../../components/tariffs/fees-list';
// import Logs from '../../components/tariffs/logs';
import {
  useChangeTariffStatusMutation,
  useLazyGetTariffItemQuery,
  useLazyGetAllTariffsQuery,
  useUpdateTariffMutation,
} from './store/services';
import { TariffAction, TariffStatus, UpdateTariffFormValues } from './types';
import { TariffsRoutes } from '../../routes';

import cn from './tariffs.module.css';

const TariffPage = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const {
    tariffs: {
      tariffPage: { generalInfo, logs, tariffs },
    },
    configurationDictionaries: { activePermissions, riskScoreRanges },
  } = useAppSelector((state) => state);
  const [errorList, setErrorList] = useState('');

  const [changeStatus, { isLoading: statusChangeInProgress, isSuccess }] =
    useChangeTariffStatusMutation();
  const [getTariff, { isLoading, isFetching }] = useLazyGetTariffItemQuery({
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [getTariffs] = useLazyGetAllTariffsQuery();
  const [updateTariff, { isLoading: changeNameInProgress }] =
    useUpdateTariffMutation();

  const { tariffId } = useParams();

  const prevStatusChangeInProgress = usePrevious(statusChangeInProgress);

  const dispatch = useAppDispatch();

  const getTariffAction = useCallback(async () => {
    const response = (await getTariff(tariffId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, [tariffId]);

  const getTariffsAction = useCallback((type: string) => {
    getTariffs(`&type=${type}`);
  }, []);

  const onStatusChange = ({
    status,
    action,
    previousTariffId,
    nextTariffId,
  }: {
    status: TariffStatus;
    action?: TariffAction;
    previousTariffId?: string;
    nextTariffId?: string;
  }) => {
    const body: {
      status: TariffStatus;
      previousTariffId?: string;
      action?: TariffAction;
      nextTariffId?: string;
    } = {
      status,
    };

    if (action) {
      body.action = action;
    }

    if (previousTariffId) {
      body.previousTariffId = previousTariffId;
    }

    if (nextTariffId) {
      body.nextTariffId = nextTariffId;
    }

    changeStatus({
      id: tariffId as string,
      body,
    });
  };

  const onUpdateTariff = (values: UpdateTariffFormValues) => {
    const formValues = {
      name: generalInfo?.name,
      type: generalInfo?.type,
      riskScoreRangeId: generalInfo?.riskScoreRangeId,
    };

    if (values.name) {
      formValues.name = values.name;
    }

    if (values.type) {
      formValues.type = values.type;
    }

    if (values.riskScoreRangeId) {
      formValues.riskScoreRangeId = values.riskScoreRangeId;
    }

    updateTariff({
      id: tariffId as string,
      body: formValues,
    });
  };

  useEffect(() => {
    if (tariffId) {
      getTariffAction();
    }
  }, [getTariffAction, tariffId]);

  useEffect(() => {
    if (!tariffs && generalInfo) {
      getTariffsAction(generalInfo?.type);
    }
  }, [generalInfo, tariffs, getTariffsAction]);

  useEffect(() => {
    if (tariffId && !logs.values) {
      dispatch(getLogs());
    }
  }, [dispatch, logs, tariffId]);

  useEffect(() => {
    if (statusChangeInProgress && prevStatusChangeInProgress && panelIsOpen) {
      setPanelIsOpen(false);
    }
  }, [statusChangeInProgress, prevStatusChangeInProgress, panelIsOpen]);

  useEffect(() => {
    let title = generalInfo?.name ? `Tariff: ${generalInfo.name}` : 'Tariff';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [generalInfo?.name]);

  return (
    <div className={cn.tariffPage}>
      <div className={cn.tariffPageLinkWrapper}>
        <NavLink
          to={TariffsRoutes.TARIFFS_LIST}
          className={cn.tariffPageBackLink}
          onClick={() => dispatch(clearTariffPageState())}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </NavLink>
      </div>
      {errorList && (
        <div className={cn.tariffError}>
          <div className={cn.tariffErrorTitle}>Unable to load data</div>
          <div className={cn.tariffErrorText}>{errorList}</div>
        </div>
      )}
      {!errorList && (
        <>
          <GeneralInfo
            isLoading={isLoading || isFetching}
            generalInfo={generalInfo}
            onStatusChange={(status) => onStatusChange(status)}
            onRiskScroreChange={(riskScore: string) =>
              onUpdateTariff({ riskScoreRangeId: riskScore })
            }
            onTariffNameChange={(name: string) => onUpdateTariff({ name })}
            onTariffTypeChange={(type: string) => onUpdateTariff({ type })}
            statusChangeInProgress={statusChangeInProgress}
            changeNameInProgress={changeNameInProgress}
            statusChangeSuccess={isSuccess}
            riskScoreRanges={riskScoreRanges?.map((item) => ({
              name: item.name,
              value: item.id,
              description: `from ${item.rangeStart} to ${item.rangeEnd}`,
            }))}
            isEditable={activePermissions?.includes(
              PermissionEnum.TARIFFS_EDIT_ACTION,
            )}
            tariffId={tariffId}
            auditLogAvailable={activePermissions?.includes(
              PermissionEnum.AUDIT_LOG_LIST,
            )}
          />
          <Fees
            isLoading={isLoading || isFetching}
            fees={generalInfo?.fees}
            tariffId={tariffId}
          />
          {/* <Logs isLoading={logs.isLoading} logs={logs.values} /> */}
        </>
      )}

      <Outlet />
    </div>
  );
};

export default TariffPage;
